import { CANVAS_WIDTH } from "../../game/constants";
import type { UiRects } from "../../game/input";
import type { RenderModel } from "../renderModel";
import { GAME_FONT } from "../renderConstants";

// ── Dependency interface ────────────────────────────────────────────

export interface OnboardingCallDeps {
  ctx: CanvasRenderingContext2D;
  canvasHeight: number;
  rafsbyPortrait: HTMLImageElement | null;
  heartLifeImage: HTMLImageElement | null;
  heartLifePremiumImage: HTMLImageElement | null;
  nowMs: () => number;
  measureTextWidth: (text: string) => number;
  drawTitle: (model: RenderModel) => UiRects;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawOnboardingCallScreen(deps: OnboardingCallDeps, model: RenderModel): UiRects {
  const {
    ctx,
    canvasHeight: canvasH,
    rafsbyPortrait,
    heartLifeImage,
    heartLifePremiumImage,
    nowMs,
    measureTextWidth,
    drawTitle,
  } = deps;

  // Draw the full title screen (city, buildings with highlights, etc.)
  const titleRects = drawTitle(model);

  const ob = model.onboardingState;
  const step = model.onboardingStep;

  // Strip building rects so tapping doesn't navigate away mid-tour
  const uiRects: UiRects = { profile: titleRects.profile, phone: titleRects.phone };

  // ── Hearts display (focused, top-center) for highlight-phone step ──
  if (step?.type === "highlight-phone") {
    const heartImg = model.staminaPremiumActive ? heartLifePremiumImage : heartLifeImage;
    const hSize = 24;
    const hText = model.staminaPremiumActive ? "UNLIMITED" : String(model.staminaCurrent);
    ctx.font = `bold 10px ${GAME_FONT}`;
    const hTextW = measureTextWidth(hText);
    const totalW = hSize + 6 + hTextW;
    const hX = (CANVAS_WIDTH - totalW) / 2;
    const hY = 14;
    // Pulsing glow behind heart
    const pulse = 0.5 + 0.5 * Math.sin(nowMs() / 300);
    ctx.save();
    ctx.shadowColor = model.staminaPremiumActive ? "#ffd700" : "#ff4466";
    ctx.shadowBlur = 8 + pulse * 6;
    if (heartImg) {
      ctx.drawImage(heartImg, hX, hY, hSize, hSize);
    }
    ctx.restore();
    ctx.fillStyle = model.staminaPremiumActive ? "#ffd700" : "#ffffff";
    ctx.font = `bold 10px ${GAME_FONT}`;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(hText, hX + hSize + 6, hY + hSize / 2);
  }

  // ── Rafsby portrait (bottom-right, flipped horizontally) ──
  const portraitSize = 144;
  const portraitPad = 4;
  const portraitX = CANVAS_WIDTH - portraitSize + 20;
  const portraitY = canvasH - portraitSize - portraitPad;

  if (rafsbyPortrait) {
    ctx.save();
    ctx.translate(portraitX + portraitSize, portraitY);
    ctx.scale(-1, 1);
    ctx.drawImage(rafsbyPortrait, 0, 0, portraitSize, portraitSize);
    ctx.restore();
  }

  // ── Speech bubble (white rounded, black text, LEFT of Rafsby) ──
  const pad = 8;
  const lineH = 10;
  const bubbleMaxX = portraitX + 20;
  const bubbleW = 140;

  let messageLines: string[] = [];

  if (ob?.active && step) {
    ctx.font = "6px " + GAME_FONT;
    const wrapW = bubbleW - pad * 2;
    const words = step.message.split(" ");
    let curLine = "";
    for (const w of words) {
      const test = curLine.length > 0 ? curLine + " " + w : w;
      if (ctx.measureText(test).width > wrapW && curLine.length > 0) {
        messageLines.push(curLine);
        curLine = w;
      } else {
        curLine = test;
      }
    }
    if (curLine.length > 0) messageLines.push(curLine);
  }

  if (messageLines.length > 0) {
    const btnRowH = 16;
    const btnGap = 4;
    const bubbleH = pad + messageLines.length * lineH + btnGap + btnRowH + pad;
    const bubbleX = bubbleMaxX - bubbleW;
    const bubbleY = portraitY + portraitSize / 4 - bubbleH - 10;

    // White rounded bubble
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.roundRect(bubbleX, bubbleY, bubbleW, bubbleH, 6);
    ctx.fill();

    // Tail triangle pointing right toward Rafsby
    ctx.beginPath();
    ctx.moveTo(bubbleX + bubbleW, bubbleY + bubbleH - 14);
    ctx.lineTo(bubbleX + bubbleW + 7, bubbleY + bubbleH - 8);
    ctx.lineTo(bubbleX + bubbleW, bubbleY + bubbleH - 4);
    ctx.closePath();
    ctx.fill();

    // Message text (black)
    ctx.fillStyle = "#111111";
    ctx.font = "6px " + GAME_FONT;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    let textY = bubbleY + pad;
    for (const ln of messageLines) {
      ctx.fillText(ln, bubbleX + pad, textY);
      textY += lineH;
    }

    // SKIP + NEXT buttons side by side
    const btnAreaW = bubbleW - pad * 2;
    const btnW = (btnAreaW - btnGap) / 2;
    const btnY = textY + btnGap;

    // SKIP button (left, subtle)
    const skipX = bubbleX + pad;
    ctx.fillStyle = "rgba(0,0,0,0.08)";
    ctx.beginPath();
    ctx.roundRect(skipX, btnY, btnW, btnRowH, 3);
    ctx.fill();
    ctx.fillStyle = "#666666";
    ctx.font = "bold 5px " + GAME_FONT;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("SKIP", skipX + btnW / 2, btnY + btnRowH / 2);
    uiRects.onboardingSkip = { x: skipX, y: btnY, w: btnW, h: btnRowH };

    // NEXT button (right, blue)
    const nextX = skipX + btnW + btnGap;
    const nextPulse = 0.8 + Math.sin(nowMs() / 400) * 0.2;
    ctx.fillStyle = "rgba(59,130,246," + nextPulse + ")";
    ctx.beginPath();
    ctx.roundRect(nextX, btnY, btnW, btnRowH, 3);
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 5px " + GAME_FONT;
    ctx.fillText("NEXT", nextX + btnW / 2, btnY + btnRowH / 2);
    // Tap zone = full screen (tap anywhere to advance)
    uiRects.onboardingTapZone = { x: 0, y: 0, w: CANVAS_WIDTH, h: canvasH };
  }

  return uiRects;
}
